// frontend/src/pages/Expenses/EditExpenses.jsx
import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  UserPlus, Building, Save, Loader2, AlertCircle, X,
  User, Tag, Info, Calendar, DollarSign, FileText, CreditCard, Hash, Banknote, Smartphone, Wallet, Layers
} from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import { useCurrency } from "../../pages/CurrencyContext";
import "./Expenses.css";

const url = process.env.REACT_APP_BACKEND_URL;

const getAuthHeaders = () => {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const getCompanyCurrency = (company) => {
  if (!company) return "USD";
  let currency = null;
  if (company.currency) {
    if (typeof company.currency === "object" && company.currency.code) currency = company.currency.code;
    else if (typeof company.currency === "string" && /^[A-Z]{3}$/.test(company.currency)) currency = company.currency;
  }
  if (!currency && company.currency_code) {
    if (typeof company.currency_code === "string" && /^[A-Z]{3}$/.test(company.currency_code)) currency = company.currency_code;
    else if (typeof company.currency_code === "object" && company.currency_code.code) currency = company.currency_code.code;
  }
  if (!currency && company.base_currency) {
    if (typeof company.base_currency === "string" && /^[A-Z]{3}$/.test(company.base_currency)) currency = company.base_currency;
    else if (typeof company.base_currency === "object" && company.base_currency.code) currency = company.base_currency.code;
  }
  if (typeof currency !== "string" || !/^[A-Z]{3}$/.test(currency)) currency = "USD";
  return currency;
};

// ✅ Auto-generation helpers
const buildAutoCode = (prefix) => {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, "0");
  const d = String(now.getDate()).padStart(2, "0");
  const rand = Math.floor(1000 + Math.random() * 9000);
  return `${prefix}-${y}${m}${d}-${rand}`;
};
const generateAutoInvoiceNumber = () => buildAutoCode("INV");
const generateAutoReferenceNumber = () => buildAutoCode("REF");

// ✅ Shared styles
const sharedFieldStyle = { display: "flex", flexDirection: "column", gap: "6px" };
const sharedInputStyle = {
  width: "100%", minHeight: "44px", padding: "10px 12px",
  borderRadius: "6px", border: "1px solid #d9d9d9",
  fontSize: "14px", boxSizing: "border-box",
  background: "#ffffff", color: "#170f48",
};
const sharedSelectWrapperStyle = { position: "relative", display: "flex", alignItems: "center" };
const sharedSelectStyle = {
  width: "100%", minHeight: "44px", padding: "10px 36px 10px 12px",
  borderRadius: "6px", border: "1px solid #d9d9d9",
  fontSize: "14px", boxSizing: "border-box",
  background: "#ffffff", appearance: "none", cursor: "pointer", color: "#170f48",
};
const sharedSelectIconStyle = {
  position: "absolute", right: "12px", top: "50%",
  transform: "translateY(-50%)", color: "#8c8c8c", pointerEvents: "none",
};
const sharedLabelStyle = {
  display: "flex", alignItems: "center", gap: "6px",
  fontSize: "13px", fontWeight: 500, color: "#170f48", margin: 0,
};
const sharedTextareaStyle = {
  width: "100%", padding: "10px 12px",
  borderRadius: "6px", border: "1px solid #d9d9d9",
  fontSize: "14px", boxSizing: "border-box",
  minHeight: "90px", fontFamily: "inherit", resize: "vertical", color: "#170f48",
};
const sharedHelperTextStyle = { fontSize: "12px", color: "#8c8c8c", marginTop: "2px" };
const sharedAutoTextStyle = { fontSize: "12px", color: "#1890ff", marginTop: "2px", fontStyle: "italic" };
const sharedErrorTextStyle = { fontSize: "12px", color: "#dc3545", marginTop: "2px" };

const EditExpenses = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { currentUser, userType, company } = useAuth();
  const { formatCurrency } = useCurrency();

  const companyCurrency = useMemo(() => getCompanyCurrency(company), [company]);

  const formatAmount = (amount) => {
    if (amount === undefined || amount === null) return formatCurrency(0, companyCurrency);
    return formatCurrency(amount, companyCurrency);
  };

  const [formData, setFormData] = useState({
    expense_name: "",
    expense_date: "",
    amount: "",
    invoice_number: "",
    description: "",
    account_id: "",
    expense_account_id: "",
    payment_method: "Cash",
    reference_number: "",
    status: "active",
  });
  const [expenseDoc, setExpenseDoc] = useState(null);
  const [allAccounts, setAllAccounts] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [paymentTypes, setPaymentTypes] = useState([]);
  const [expenseTypeAccounts, setExpenseTypeAccounts] = useState([]);
  const [loadingExpenseTypes, setLoadingExpenseTypes] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingAccounts, setLoadingAccounts] = useState(false);
  const [errors, setErrors] = useState({});

  // ✅ Stable auto previews
  const [autoInvoicePreview] = useState(() => generateAutoInvoiceNumber());
  const [autoReferencePreview] = useState(() => generateAutoReferenceNumber());

  const paymentMethodOptions = [
    { value: "Cash", label: "Cash" },
    { value: "Bank", label: "Bank" },
    { value: "Mobile Money", label: "Mobile Money" },
    { value: "Credit", label: "Credit" }
  ];

  const fetchPaymentTypes = async (companyId) => {
    if (!companyId) return;
    try {
      const response = await axios.get(`${url}/api/paymenttype/dropdown`, {
        headers: getAuthHeaders(),
        params: { company_id: companyId, include_inactive: false }
      });
      if (response.data.success) setPaymentTypes(response.data.data || []);
    } catch (error) {} finally {}
  };

  const fetchAllAccounts = async (companyId) => {
    if (!companyId) return;
    try {
      setLoadingAccounts(true);
      const response = await axios.get(`${url}/api/account/dropdown`, {
        headers: getAuthHeaders(),
        params: { company_id: companyId, type: "Asset", include_inactive: false }
      });
      if (response.data.success) {
        const loadedAccounts = response.data.data || [];
        setAllAccounts(loadedAccounts);
        filterAccountsByPaymentMethod(formData.payment_method, loadedAccounts);
      }
    } catch (error) {} finally { setLoadingAccounts(false); }
  };

  const fetchExpenseTypeAccounts = async (companyId) => {
    if (!companyId) return;
    try {
      setLoadingExpenseTypes(true);
      const response = await axios.get(`${url}/api/account/dropdown`, {
        headers: getAuthHeaders(),
        params: { company_id: companyId, type: "Expense", include_inactive: "false" },
      });
      if (response.data.success) {
        const list = (response.data.data || []).filter((acc) => {
          const code = String(acc.account_code || "");
          return code.startsWith("5") || code.startsWith("6") || code.startsWith("7");
        });
        list.sort((a, b) => String(a.account_code).localeCompare(String(b.account_code), undefined, { numeric: true }));
        setExpenseTypeAccounts(list);
      }
    } catch (error) { setExpenseTypeAccounts([]); } finally { setLoadingExpenseTypes(false); }
  };

  const filterAccountsByPaymentMethod = (paymentMethodValue, sourceAccounts = allAccounts) => {
    if (!paymentMethodValue) { setAccounts([]); return; }
    const filtered = sourceAccounts.filter((account) => {
      if (account.is_control_account === true || !account.parent_account) return false;
      const name = (account.account_name || "").toLowerCase();
      if (paymentMethodValue === "Cash") return name.includes("cash") || name.includes("petty");
      if (paymentMethodValue === "Bank") return name.includes("bank");
      if (paymentMethodValue === "Mobile Money") return name.includes("mobile") || name.includes("evc") || name.includes("e-dahab") || name.includes("wallet");
      if (paymentMethodValue === "Credit") return name.includes("credit") || name.includes("payable") || name.includes("receivable");
      return false;
    });
    setAccounts(filtered);
    if (formData.account_id && !filtered.some((a) => a._id === formData.account_id)) {
      setFormData(prev => ({ ...prev, account_id: "" }));
    }
  };

  const fetchExpense = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${url}/api/expense/${id}`, { headers: getAuthHeaders() });

      if (res.data.success) {
        setExpenseDoc(res.data.data);
        const expense = res.data.data;

        setFormData({
          expense_name: expense.expense_name || "",
          expense_date: expense.expense_date
            ? new Date(expense.expense_date).toISOString().split('T')[0]
            : "",
          amount: expense.amount || "",
          invoice_number: typeof expense.invoice_number === "string" ? expense.invoice_number : "",
          description: expense.description || "",
          account_id: expense.account_id?._id || expense.account_id || "",
          expense_account_id: expense.expense_account_id?._id || expense.expense_account_id || "",
          payment_method: expense.payment_method || "Cash",
          reference_number: expense.reference_number || "",
          status: expense.status || "active",
        });

        const companyId = expense.company_id?._id || expense.company_id;
        if (companyId) {
          await Promise.all([
            fetchPaymentTypes(companyId),
            fetchAllAccounts(companyId),
            fetchExpenseTypeAccounts(companyId),
          ]);
          filterAccountsByPaymentMethod(expense.payment_method || "Cash");
        }
      } else {
        toast.error(res.data.message || "Failed to load expense");
        navigate("/ViewExpenses");
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to load expense");
      navigate("/ViewExpenses");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchExpense(); /* eslint-disable-next-line */ }, [id]);

  useEffect(() => {
    if (formData.payment_method) {
      filterAccountsByPaymentMethod(formData.payment_method);
    } else {
      setAccounts(allAccounts);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.payment_method, allAccounts]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "payment_method") {
      setFormData(prev => ({ ...prev, payment_method: value, account_id: "" }));
      filterAccountsByPaymentMethod(value);
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: "" }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.expense_name.trim()) newErrors.expense_name = "Expense name is required";
    if (!formData.expense_date) newErrors.expense_date = "Expense date is required";
    if (!formData.amount) newErrors.amount = "Amount is required";
    else if (parseFloat(formData.amount) <= 0) newErrors.amount = "Amount must be greater than 0";
    if (!formData.expense_account_id) newErrors.expense_account_id = "Expense type is required";
    if (!formData.account_id) newErrors.account_id = "Payment account is required";
    if (Object.keys(newErrors).length > 0) { setErrors(newErrors); return false; }
    setErrors({}); return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) { toast.error("Please fix form errors"); return; }

    setSubmitting(true);
    try {
      // ✅ Auto-fill empty fields
      const finalInvoiceNumber =
        formData.invoice_number?.trim() || autoInvoicePreview;

      const finalReferenceNumber =
        formData.reference_number?.trim() || autoReferencePreview;

      const finalDescription =
        formData.description?.trim() ||
        `${finalInvoiceNumber} - ${finalReferenceNumber}`;

      const updateData = {
        expense_name: formData.expense_name.trim(),
        expense_date: formData.expense_date,
        amount: parseFloat(formData.amount),
        invoice_number: finalInvoiceNumber,
        description: finalDescription,
        account_id: formData.account_id,
        expense_account_id: formData.expense_account_id,
        payment_method: formData.payment_method,
        reference_number: finalReferenceNumber,
        status: formData.status
      };

      const res = await axios.put(`${url}/api/expense/${id}`, updateData, { headers: getAuthHeaders() });

      if (res.data.success) {
        toast.success(res.data.message || "Expense updated successfully!");
        if (res.data.data?.account?.balance) {
          toast.info(`New account balance: ${formatAmount(res.data.data.account.balance)}`);
        }
        setTimeout(() => navigate("/ViewExpenses"), 1000);
      } else {
        toast.error(res.data.message || "Update failed");
        setSubmitting(false);
      }
    } catch (err) {
      if (err.response) {
        const errorMsg = err.response.data?.message || "Update failed";
        toast.error(errorMsg);
        if (err.response.status === 409) setErrors(prev => ({ ...prev, expense_name: "Expense name already exists for this company" }));
        if (err.response.status === 400 && errorMsg.includes("Insufficient balance")) setErrors(prev => ({ ...prev, account_id: errorMsg }));
      } else {
        toast.error("Error: " + err.message);
      }
      setSubmitting(false);
    }
  };

  const handleCancel = () => navigate("/ViewExpenses");

  const selectedAccount = accounts.find(acc => acc._id === formData.account_id);
  const selectedExpenseType = expenseTypeAccounts.find(acc => acc._id === formData.expense_account_id);

  if (loading) {
    return (
      <div className="form-wrapper">
        <div className="expense-form-container">
          <div className="loading-container">
            <Loader2 className="animate-spin" size={32} />
            <p>Loading expense details...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!expenseDoc) {
    return (
      <div className="form-wrapper">
        <div className="expense-form-container">
          <div className="error-container">
            <AlertCircle size={32} color="#dc3545" />
            <p>Expense not found</p>
            <button className="cancel-btn" onClick={() => navigate("/ViewExpenses")}>Back to List</button>
          </div>
        </div>
      </div>
    );
  }

  const canEdit = formData.status === "active";

  return (
    <div className="form-wrapper">
      <div className="expense-form-container">
        <button className="close-btn" onClick={handleCancel} title="Go Back" disabled={submitting}>
          <X size={20} />
        </button>

        <div className="form-header">
          <UserPlus size={24} />
          <h2 className="form-title">Edit Expense #{expenseDoc.expense_id}</h2>
          <p className="form-subtitle">
            Update expense details for {expenseDoc?.expense_name || 'expense'}
          </p>
        </div>

        <form className="expense-form" onSubmit={handleSubmit}>
          <div className="form-grid">
            <div style={sharedFieldStyle} className={errors.expense_name ? 'error' : ''}>
              <input
                type="text" name="expense_name" value={formData.expense_name}
                onChange={handleChange} placeholder="Enter expense name"
                required disabled={!canEdit || submitting} style={sharedInputStyle}
              />
              <label style={sharedLabelStyle}><Tag size={14} />Expense Name *</label>
              {errors.expense_name && <small style={sharedErrorTextStyle}>{errors.expense_name}</small>}
            </div>

            <div style={sharedFieldStyle} className={errors.expense_date ? 'error' : ''}>
              <input
                type="date" name="expense_date" value={formData.expense_date}
                onChange={handleChange} required disabled={!canEdit || submitting}
                style={sharedInputStyle}
              />
              <label style={sharedLabelStyle}><Calendar size={14} />Expense Date *</label>
              {errors.expense_date && <small style={sharedErrorTextStyle}>{errors.expense_date}</small>}
            </div>

            <div style={sharedFieldStyle} className={errors.amount ? 'error' : ''}>
              <input
                type="number" name="amount" value={formData.amount}
                onChange={handleChange} placeholder="Enter amount" step="0.01" min="0.01"
                required disabled={!canEdit || submitting} style={sharedInputStyle}
              />
              <label style={sharedLabelStyle}><DollarSign size={14} />Amount *</label>
              {errors.amount && <small style={sharedErrorTextStyle}>{errors.amount}</small>}
            </div>

            {/* ✅ Invoice Number with auto-fill preview */}
            <div style={sharedFieldStyle}>
              <input
                type="text" name="invoice_number" value={formData.invoice_number}
                onChange={handleChange} placeholder={autoInvoicePreview}
                disabled={!canEdit || submitting} style={sharedInputStyle}
              />
              <label style={sharedLabelStyle}><FileText size={14} />Invoice Number</label>
              {formData.invoice_number?.trim() ? (
                <small style={{ ...sharedHelperTextStyle, color: "#52c41a" }}>
                  Manual entry: "{formData.invoice_number}"
                </small>
              ) : (
                <small style={sharedAutoTextStyle}>
                  Empty → auto: {autoInvoicePreview}
                </small>
              )}
            </div>

            <div style={sharedFieldStyle}>
              <div style={sharedSelectWrapperStyle}>
                <select
                  name="payment_method" value={formData.payment_method}
                  onChange={handleChange} required disabled={!canEdit || submitting}
                  style={sharedSelectStyle}
                >
                  <option value="">Select Payment Method</option>
                  {paymentMethodOptions.map((method) => (
                    <option key={method.value} value={method.value}>{method.label}</option>
                  ))}
                </select>
                <CreditCard size={16} style={sharedSelectIconStyle} />
              </div>
              <label style={sharedLabelStyle}><CreditCard size={14} />Payment Method *</label>
            </div>

            <div style={sharedFieldStyle} className={errors.expense_account_id ? 'error' : ''}>
              <div style={sharedSelectWrapperStyle}>
                <select
                  name="expense_account_id" value={formData.expense_account_id}
                  onChange={handleChange} required
                  disabled={loadingExpenseTypes || !canEdit || submitting || expenseTypeAccounts.length === 0}
                  style={{
                    ...sharedSelectStyle,
                    background: loadingExpenseTypes || expenseTypeAccounts.length === 0 ? "#f5f5f5" : "#ffffff",
                  }}
                >
                  <option value="">
                    {loadingExpenseTypes ? "Loading expense types..."
                      : expenseTypeAccounts.length === 0 ? "No expense accounts configured"
                      : "Select Expense Type"}
                  </option>
                  {expenseTypeAccounts.map((acc) => (
                    <option key={acc._id} value={acc._id}>
                      {acc.account_code} - {acc.account_name}
                    </option>
                  ))}
                </select>
                <Layers size={16} style={sharedSelectIconStyle} />
              </div>
              <label style={sharedLabelStyle}><Layers size={14} />Expense Type *</label>
              {errors.expense_account_id && <small style={sharedErrorTextStyle}>{errors.expense_account_id}</small>}
              {!errors.expense_account_id && selectedExpenseType && (
                <small style={{ ...sharedHelperTextStyle, color: "#52c41a" }}>
                  Journal entry will debit: {selectedExpenseType.account_code} - {selectedExpenseType.account_name}
                </small>
              )}
            </div>

            <div style={sharedFieldStyle} className={errors.account_id ? 'error' : ''}>
              <div style={sharedSelectWrapperStyle}>
                <select
                  name="account_id" value={formData.account_id}
                  onChange={handleChange} required
                  disabled={loadingAccounts || !canEdit || submitting || !formData.payment_method}
                  style={{
                    ...sharedSelectStyle,
                    background: loadingAccounts || !formData.payment_method ? "#f5f5f5" : "#ffffff",
                  }}
                >
                  <option value="">Select Payment Account</option>
                  {accounts.map((acc) => (
                    <option key={acc._id} value={acc._id}>
                      {acc.account_name} - Balance: {formatAmount(acc.balance)}
                    </option>
                  ))}
                </select>
                <Info size={16} style={sharedSelectIconStyle} />
              </div>
              <label style={sharedLabelStyle}><Wallet size={14} />Payment Account *</label>
              {errors.account_id && <small style={sharedErrorTextStyle}>{errors.account_id}</small>}
              {selectedAccount && (
                <small style={{ ...sharedHelperTextStyle, color: "#52c41a" }}>
                  This account will be credited in the journal
                </small>
              )}
            </div>

            {selectedAccount && formData.amount && parseFloat(formData.amount) > 0 && (
              <div
                className="balance-info"
                style={{
                  gridColumn: "span 2",
                  padding: "12px 16px",
                  background: selectedAccount.balance >= parseFloat(formData.amount) ? "#e6f7ff" : "#fff2f0",
                  borderRadius: "6px",
                  borderLeft: `4px solid ${selectedAccount.balance >= parseFloat(formData.amount) ? "#1890ff" : "#ff4d4f"}`,
                  fontSize: "13px",
                  display: "flex", justifyContent: "space-between", alignItems: "center",
                  gap: "16px", flexWrap: "wrap",
                }}
              >
                <span><strong>Account Balance:</strong> {formatAmount(selectedAccount.balance)}</span>
                <span><strong>Balance After:</strong> {formatAmount(selectedAccount.balance - parseFloat(formData.amount))}</span>
                {selectedAccount.balance < parseFloat(formData.amount) && (
                  <span style={{ color: "#ff4d4f", fontWeight: 600 }}>⚠️ Insufficient balance!</span>
                )}
              </div>
            )}

            {/* ✅ Reference Number with auto-fill preview */}
            <div style={sharedFieldStyle}>
              <input
                type="text" name="reference_number" value={formData.reference_number}
                onChange={handleChange} placeholder={autoReferencePreview}
                disabled={!canEdit || submitting} style={sharedInputStyle}
              />
              <label style={sharedLabelStyle}><Hash size={14} />Reference Number</label>
              {formData.reference_number?.trim() ? (
                <small style={{ ...sharedHelperTextStyle, color: "#52c41a" }}>
                  Manual entry: "{formData.reference_number}"
                </small>
              ) : (
                <small style={sharedAutoTextStyle}>
                  Empty → auto: {autoReferencePreview}
                </small>
              )}
            </div>

            {/* ✅ Description with auto-fill preview */}
            <div style={{ ...sharedFieldStyle, gridColumn: "span 2" }}>
              <textarea
                name="description" value={formData.description}
                onChange={handleChange}
                placeholder={`${formData.invoice_number?.trim() || autoInvoicePreview} - ${formData.reference_number?.trim() || autoReferencePreview}`}
                rows="3" disabled={!canEdit || submitting}
                style={sharedTextareaStyle}
              />
              <label style={sharedLabelStyle}><FileText size={14} />Description</label>
              {formData.description?.trim() ? (
                <small style={{ ...sharedHelperTextStyle, color: "#52c41a" }}>
                  Manual entry
                </small>
              ) : (
                <small style={sharedAutoTextStyle}>
                  Empty → auto: {formData.invoice_number?.trim() || autoInvoicePreview} - {formData.reference_number?.trim() || autoReferencePreview}
                </small>
              )}
            </div>

            <div style={sharedFieldStyle}>
              <div style={sharedSelectWrapperStyle}>
                <select
                  name="status" value={formData.status}
                  onChange={handleChange} disabled={submitting}
                  style={sharedSelectStyle}
                >
                  <option value="active">Active</option>
                  <option value="paid">Paid</option>
                  <option value="inactive">Inactive</option>
                </select>
                <AlertCircle size={16} style={sharedSelectIconStyle} />
              </div>
              <label style={sharedLabelStyle}><AlertCircle size={14} />Status</label>
            </div>
          </div>

          {formData.status === 'inactive' && (
            <div className="warning-message">
              <AlertCircle size={16} />
              <span>This expense is inactive. Reactivate to edit or change status.</span>
            </div>
          )}

          <div className="form-actions">
            <button type="button" className="cancel-btn" onClick={handleCancel} disabled={submitting}>Cancel</button>
            <button
              type="submit"
              className="submit-btn"
              disabled={submitting || (!canEdit && formData.status === 'inactive') || (selectedAccount && selectedAccount.balance < parseFloat(formData.amount || 0))}
            >
              {submitting ? (
                <><Loader2 className="animate-spin mr-2" size={16} />Updating...</>
              ) : (
                <>Update Expense<Save className="ml-2" size={16} /></>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditExpenses;
